import * as React from 'react'
import { useState, useCallback, useMemo } from 'react'
import { Formik, FormikErrors, FormikHelpers } from 'formik'
import { SideDrawer, FilterIcon } from '@sales-center/components'
import { getStyles, getButtonStyles } from './useStyles'
import { Filters } from './Filters'
import { IFilterSet, IOrderBy } from './typings'
import { SearchField } from './SearchField'

import Button, { buttonClasses } from '@mui/material/Button'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'

interface Props<T> {
  applyCallback: (model: T) => void
  initialValues: T
  defaultValues: T
  clearedValues: T
  filterComponent: React.ComponentType
  orderOptions: IOrderBy[]
  validate?: (model: T) => FormikErrors<T>
  hideOrderBy?: boolean
  disabled?: boolean
}

export const FilterButton = <U, T extends IFilterSet<U>>(props: Props<T>) => {
  const { applyCallback, disabled = false } = props

  const { icon } = getStyles(useTheme())

  const includeSearchField = useMemo(
    () => !!props.defaultValues.search,
    [props.defaultValues.search],
  )

  const { filterCount, active } = useMemo(() => {
    const filterCount = props.initialValues.appliedCount
    const active = filterCount > 0
    return { filterCount, active }
  }, [props.initialValues.appliedCount])

  const [reset, setReset] = useState(false)
  const [open, setOpen] = useState(false)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  const onSubmit = useCallback(
    (model: T, { setSubmitting }: FormikHelpers<T>) => {
      applyCallback(model)
      setSubmitting(false)
      onClose()
    },
    [applyCallback, onClose],
  )

  const onReset = useCallback(
    (v: boolean) => {
      setReset(v)
    },
    [setReset],
  )
  const styles = getButtonStyles(useTheme())
  return (
    <Box display="flex" alignItems="center">
      {includeSearchField && (
        <Formik {...props} onSubmit={onSubmit} enableReinitialize>
          <SearchField disabled={disabled} />
        </Formik>
      )}
      <Button
        color="primary"
        variant={active ? 'contained' : 'outlined'}
        sx={{
          [`&.${buttonClasses.root}`]: { ...styles.root },
          ...styles.label,
          [`& .${buttonClasses.outlined}`]: { ...styles.outlined },
        }}
        onClick={onOpen}
        disabled={disabled}
        data-test-id="filterBtn"
      >
        <FilterIcon sx={icon} />
        {'Filters'}
        {active && ` (${filterCount})`}
      </Button>
      <SideDrawer title="Filters" open={open} onClose={onClose} width={420}>
        <Formik {...props} onSubmit={onSubmit} onReset={() => onReset(true)}>
          <Filters<U, T>
            {...props}
            onClose={onClose}
            reset={reset}
            onFocus={() => onReset(false)}
          />
        </Formik>
      </SideDrawer>
    </Box>
  )
}
FilterButton.displayName = 'FilterButton'
